import { AuthorPage } from '@gik/blog/components/AuthorPage/AuthorPage';
import type { StaticProps } from '@gik/core/api/BaseAPIConfig';
import { ISRRevalidateTimeout, baseFallback } from '@gik/core/api/BaseAPIConfig';
import { useNavigatableInitialData } from '@gik/core/hooks/hooks/useNavigatableInitialData';
import { NotFoundPage } from '@gik/core/pages/NotFound/NotFoundPage';
import { catchSSRError } from '@gik/core/utils/catchSSRError';
import { measureSSRPerformance } from '@gik/core/utils/measureSSRPerformance';
import { getAuthor } from '@/api/author/author';
import { getWordPressAuthorSlugs } from '@/api/author/getWordpressAuthorSlugs';
import { getBlogNavInitialData } from '@/api/blog/blogNav';
import { getAuthorPosts } from '@/api/posts/authorPosts';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { BlogLayout } from '@/components/layout/withBlogLayout';
import { NotFoundError } from '@/utils/handleSSRError';
import type { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { SWRConfig } from 'swr';

export default function AuthorPageWrapper(props): React.ReactElement {
  const router = useRouter();
  const fallback = useNavigatableInitialData(props);
  if (router.isFallback) {
    // TODO: wrap each page with withFallback function
    return <div>Loading...</div>;
  }

  // TODO: move logic to getStaticProps (throw error, see product page for example)
  if (router.query.slug && fallback?.[fallback?.authorKey] && !fallback?.[fallback?.authorKey].length) {
    return <NotFoundPage />;
  }

  return (
    <SWRConfig value={{ fallback: props.fallback }}>
      <AuthorPage slug={router.query.slug as string} />
    </SWRConfig>
  );
}

AuthorPageWrapper.getLayout = function getLayout(page, layoutProps: PageLayoutProps) {
  return <BlogLayout {...layoutProps}>{page}</BlogLayout>;
};

async function _getStaticProps(context: GetServerSidePropsContext): Promise<StaticProps> {
  const slug = context.params.slug as string;

  const authorResponse = getAuthor(slug);
  const { situationsResponse, tagsResponse } = getBlogNavInitialData();

  const [author, situations, tags] = await Promise.all([
    authorResponse.json,
    situationsResponse.json,
    tagsResponse.json,
  ]);

  if (!author?.length) {
    throw new NotFoundError();
  }

  const authorPostsResponse = author?.length ? getAuthorPosts(author[0].slug) : null;

  return baseFallback(
    {
      [authorResponse.key]: author,
      [situationsResponse.key]: situations,
      [tagsResponse.key]: tags,
      ...(author.length
        ? {
            [authorPostsResponse.key]: await authorPostsResponse.json,
          }
        : {}),
    },
    {
      authorKey: authorResponse.key,
    },
    ISRRevalidateTimeout.sitemapSlow
  );
}

export const getStaticProps = catchSSRError(measureSSRPerformance(_getStaticProps));

export async function getStaticPaths() {
  if (process.env.RENDER_STATIC_PAGES_AT_BUILD_TIME === 'true') {
    const authors = await getWordPressAuthorSlugs();
    const paths = authors.map(({ slug }) => ({
      params: { slug },
    }));
    return { paths, fallback: true };
  } else {
    return { paths: [], fallback: 'blocking' };
  }
}
